// Octo.Mobile src/api/client.ts deseninin web uyarlaması: runtime baseURL, Bearer, tek-uçuşlu 401 refresh,
// X-Octo-Connection kiracı başlığı, iş hatası (HTTP 200 + success:false) normalizasyonu.
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { runtimeConfig } from '@/app/config/runtimeConfig';
import { authHeaders, forceLogout, getAccessToken, refreshAccessToken } from './tokenProvider';
import { OctoApiError, splitSqlMessage, toOctoError } from './errors';
import { refreshOnce } from './refreshSingleFlight';
import { isTokenExpiring } from './tokenExpiry';
import { logClientEvent } from '@/shared/lib/logging/clientLog';
import { hubIdentity } from '@/shared/realtime/hubIdentity';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** `octo.expectSuccess=false` → gövdedeki success:false hata sayılmaz (login gibi uçlar). */
    octo?: { expectSuccess?: boolean; skipAuth?: boolean };
    _retry?: boolean;
  }
}

const TIMEOUT = 30_000;
const AUTH_PATHS = ['/api/auth/login', '/api/auth/refresh', '/api/connections'];

function isAuthPath(url: string) {
  return AUTH_PATHS.some((p) => url.includes(p));
}

class ApiClient {
  readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({ timeout: TIMEOUT, headers: { 'Content-Type': 'application/json' } });

    this.instance.interceptors.request.use(async (config) => {
      config.baseURL = runtimeConfig().dalUrl;
      const url = config.url ?? '';
      /*
       * Süresi dolmak üzere olan token İSTEKTEN ÖNCE yenilenir.
       *
       * Yenileme yalnızca 401'e bağlıyken, kimlik zorlaması kapalı sunucularda
       * (Auth:Enforce=false) süresi dolan token 401 üretmiyor: istek geçiyor ama
       * sunucu tarafında kimliksiz oluyor ve rol yetkileri boş dönüyor — kullanıcı
       * yetkisi olduğu hâlde Yeni/Düzenle/Sil düğmelerini kaybediyordu.
       */
      if (!isAuthPath(url) && !config.octo?.skipAuth && isTokenExpiring(getAccessToken())) {
        try {
          await refreshOnce(refreshAccessToken);
        } catch {
          /* yenileme başarısızsa istek eldeki token'la denenir; 401 akışı devrede */
        }
      }
      const headers = authHeaders();
      if (isAuthPath(url) || config.octo?.skipAuth) delete headers['Authorization'];
      Object.assign(config.headers, headers, { 'Accept-Language': document.documentElement.lang || 'tr' });
      // Canlı satır senkronu: DAL yayını bu sekmeye geri göndermesin (bkz. hubIdentity).
      const hub = hubIdentity.get();
      if (hub) config.headers['X-Octo-Hub'] = hub;
      return config;
    });

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        const data = response.data as { success?: boolean; message?: string; detail?: string } | null;
        const expectSuccess = response.config.octo?.expectSuccess ?? true;
        if (expectSuccess && data && typeof data === 'object' && data.success === false) {
          const { message, detail } = splitSqlMessage(data.message ?? 'İşlem başarısız.');
          throw new OctoApiError('business', message, {
            status: response.status,
            detail: data.detail ?? detail,
            url: response.config.url,
            raw: data,
          });
        }
        return response;
      },
      async (error) => {
        const original = error?.config as AxiosRequestConfig | undefined;
        const url = original?.url ?? '';
        if (error?.response?.status === 401 && original && !original._retry && !isAuthPath(url)) {
          const err = toOctoError(error);
          if (err.kind === 'tenant') {
            forceLogout('tenant');
            throw err;
          }
          original._retry = true;
          const token = await refreshOnce(refreshAccessToken);
          if (token) {
            original.headers = {
              ...(original.headers as Record<string, string>),
              Authorization: `Bearer ${token}`,
            };
            return this.instance(original);
          }
          forceLogout('expired');
        }
        const octoError = toOctoError(error);
        /*
         * SUNUCU HATASI ve AĞ HATASI kayda girer; iş kuralı hataları girmez.
         *
         * Kullanıcının yanlış veri girmesi (business/auth) günlüğe yazılacak bir olay
         * değil — onu yazmak Seq'i gürültüyle doldurur ve gerçek arızayı görünmez kılar.
         * 5xx ve "sunucuya ulaşılamıyor" ise arızadır: kullanıcı aramadan önce kayıtta
         * durmalı. Sunucunun ürettiği kimlik taşınır ki iki taraf aynı satırda buluşsun.
         */
        const status = octoError.status ?? 0;
        if (octoError.kind === 'network' || status >= 500) {
          logClientEvent({
            source: 'api',
            level: 'error',
            kind: octoError.kind === 'network' ? 'NetworkError' : `Http${status}`,
            message: `${status || 'AĞ'} ${original?.method?.toUpperCase() ?? ''} ${url} — ${octoError.message}`,
            stack: octoError.detail,
            errorId: octoError.errorId,
          });
        }
        throw octoError;
      },
    );
  }

  get<T>(url: string, config?: AxiosRequestConfig) {
    return this.instance.get<T>(url, config).then((r) => r.data);
  }
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.instance.post<T>(url, data, config).then((r) => r.data);
  }
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.instance.put<T>(url, data, config).then((r) => r.data);
  }
  delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.instance.delete<T>(url, config).then((r) => r.data);
  }
  /** İkili içerik (ikon, PDF, export). */
  getBlob(url: string, config?: AxiosRequestConfig) {
    return this.instance.get<Blob>(url, { ...config, responseType: 'blob' }).then((r) => r.data);
  }
  /** Şu anki access token (SSE/SignalR gibi axios dışı kullanımlar için). */
  get accessToken() {
    return getAccessToken();
  }
}

export const apiClient = new ApiClient();
